import React from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Sparkles, Loader2, Target, RefreshCw, Download, Mail, Phone, CheckCircle2, Lock, Eye, Zap } from 'lucide-react';

export function LeadDiscoverDialog({
  discoverOpen,
  setDiscoverOpen,
  discovering,
  discoveredContacts,
  selectedDiscovered,
  importing,
  handleDiscoverLeads,
  handleImportSelected,
  selectAllDiscovered,
  toggleDiscoveredSelection,
}) {
  return (
    <Dialog open={discoverOpen} onOpenChange={setDiscoverOpen}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col p-0" data-testid="discover-dialog">
        <div className="px-6 pt-6 pb-4 border-b border-border">
          <DialogHeader className="space-y-1">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500/10 to-blue-600/10 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-cyan-500" />
              </div>
              Discover New Leads
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              ICP-matched contacts ready to import. Reveal contact details for <span className="font-medium text-foreground">$0.065</span> each.
            </p>
          </DialogHeader>
        </div>

        {discovering ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
            </div>
            <p className="text-sm text-muted-foreground">Searching for ICP-matched contacts...</p>
          </div>
        ) : discoveredContacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center">
              <Target className="h-6 w-6 text-muted-foreground/50" />
            </div>
            <p className="text-muted-foreground">No new contacts found matching your ICP.</p>
            <Button variant="outline" size="sm" onClick={handleDiscoverLeads}>
              <RefreshCw className="h-4 w-4 mr-2" /> Try Again
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-6 py-3 border-b border-border bg-muted/30">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={selectedDiscovered.size === discoveredContacts.length}
                  onCheckedChange={selectAllDiscovered}
                  data-testid="select-all-discovered"
                />
                <span className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{selectedDiscovered.size}</span> of {discoveredContacts.length} selected
                </span>
              </div>
              <Button
                size="sm"
                disabled={selectedDiscovered.size === 0 || importing}
                onClick={handleImportSelected}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-sm"
                data-testid="import-discovered-btn"
              >
                {importing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Import {selectedDiscovered.size > 0 ? `(${selectedDiscovered.size})` : ''}
              </Button>
            </div>

            <div className="overflow-y-auto flex-1 px-3 py-2" style={{maxHeight: '55vh'}}>
              <div className="space-y-1">
                {discoveredContacts.map((contact, idx) => {
                  const isSelected = selectedDiscovered.has(idx);
                  const grade = contact.icp_score?.grade;
                  const gradeColors = grade === 'A'
                    ? 'bg-emerald-500/10 text-emerald-500 ring-emerald-500/20 dark:bg-emerald-500/15 dark:text-emerald-400'
                    : grade === 'B'
                      ? 'bg-cyan-500/10 text-cyan-500 ring-cyan-500/20 dark:bg-cyan-500/15 dark:text-cyan-400'
                      : grade === 'C'
                        ? 'bg-amber-500/10 text-amber-500 ring-amber-500/20 dark:bg-amber-500/15 dark:text-amber-400'
                        : 'bg-muted text-muted-foreground ring-border';
                  return (
                    <div
                      key={idx}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all duration-150 ${
                        isSelected
                          ? 'border-cyan-500/40 bg-cyan-500/[0.06] dark:bg-cyan-500/[0.08]'
                          : 'border-transparent hover:bg-muted/50'
                      }`}
                      onClick={() => toggleDiscoveredSelection(idx)}
                      data-testid={`discovered-contact-${idx}`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleDiscoveredSelection(idx)}
                        className="shrink-0"
                      />

                      {contact.icp_score && (
                        <div className={`flex flex-col items-center justify-center w-10 h-10 rounded-xl ring-1 shrink-0 ${gradeColors}`} data-testid={`icp-score-${idx}`}>
                          <span className="text-xs font-bold leading-none">{contact.icp_score.total}%</span>
                          <span className="text-[8px] font-semibold leading-none mt-0.5 uppercase">{grade}</span>
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-foreground truncate">{contact.name}</span>
                          {contact.has_email && (
                            <Mail className="h-3 w-3 text-emerald-500 shrink-0" data-testid={`email-avail-${idx}`} />
                          )}
                          {contact.has_phone && (
                            <Phone className="h-3 w-3 text-emerald-500 shrink-0" data-testid={`phone-avail-${idx}`} />
                          )}
                          {contact.auto_revealed && (
                            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-500 dark:text-emerald-400 border border-emerald-500/20" data-testid={`auto-revealed-badge-${idx}`}>Auto-Revealed</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {contact.title}{contact.company ? ` at ${contact.company}` : ''}
                        </p>
                        {contact.icp_score?.breakdown && (
                          <div className="flex items-center gap-1.5 mt-1.5">
                            {contact.seniority && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium">{contact.seniority}</span>
                            )}
                            {contact.icp_score.breakdown.title > 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-medium">Title Match</span>
                            )}
                            {contact.icp_score.breakdown.location > 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-600 dark:text-violet-400 font-medium">Location</span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col items-end gap-1 shrink-0 text-right">
                        {contact.city && (
                          <span className="text-[11px] text-muted-foreground">{contact.city}{contact.country ? `, ${contact.country}` : ''}</span>
                        )}
                        {contact.auto_revealed ? (
                          <div className="flex items-center gap-1 text-emerald-500">
                            <CheckCircle2 className="h-3 w-3" />
                            <span className="text-[11px] font-medium">Free</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Lock className="h-3 w-3 text-amber-500" />
                            <span className="text-[11px] font-semibold text-amber-500 dark:text-amber-400">$0.065</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function BulkRevealDialog({
  bulkRevealConfirm,
  setBulkRevealConfirm,
  confirmBulkReveal,
}) {
  return (
    <Dialog open={!!bulkRevealConfirm} onOpenChange={(open) => { if (!open) setBulkRevealConfirm(null); }}>
      <DialogContent className="max-w-sm" data-testid="bulk-reveal-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-amber-500" />
            Reveal All Contacts
          </DialogTitle>
        </DialogHeader>
        {bulkRevealConfirm && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Reveal <span className="font-semibold text-foreground">{bulkRevealConfirm.count}</span> unrevealed contacts in <span className="font-semibold text-foreground">"{bulkRevealConfirm.columnName}"</span> column?
            </p>
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
              <span className="text-sm text-amber-700">Estimated cost</span>
              <span className="text-sm font-semibold text-amber-700">${bulkRevealConfirm.cost}</span>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setBulkRevealConfirm(null)} data-testid="bulk-reveal-cancel-btn">
                Cancel
              </Button>
              <Button size="sm" onClick={confirmBulkReveal} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="bulk-reveal-confirm-btn">
                <Eye className="h-3.5 w-3.5 mr-1.5" />
                Reveal {bulkRevealConfirm.count} Contacts
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function BulkIcpRevealDialog({
  bulkIcpRevealOpen,
  setBulkIcpRevealOpen,
  bulkIcpRevealing,
  confirmBulkIcpReveal,
}) {
  return (
    <Dialog open={!!bulkIcpRevealOpen && Array.isArray(bulkIcpRevealOpen)} onOpenChange={(open) => { if (!open) setBulkIcpRevealOpen(false); }}>
      <DialogContent className="max-w-md" data-testid="bulk-icp-reveal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            Reveal Top ICP Leads
          </DialogTitle>
        </DialogHeader>
        {Array.isArray(bulkIcpRevealOpen) && bulkIcpRevealOpen.length > 0 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Reveal contact details for your <span className="font-semibold text-foreground">top {bulkIcpRevealOpen.length}</span> highest-scored ICP leads:
            </p>
            <div className="space-y-2 max-h-[240px] overflow-y-auto">
              {bulkIcpRevealOpen.map((lead, idx) => (
                <div key={lead.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/50 border border-border" data-testid={`bulk-icp-lead-${idx}`}>
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-bold shrink-0">
                    {lead._bestIcpScore}%
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{lead.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{lead.title || lead.designation || ''}{lead.company ? ` at ${lead.company}` : ''}</p>
                  </div>
                  <Target className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
              <span className="text-sm text-amber-700 dark:text-amber-400">Estimated cost</span>
              <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">${(bulkIcpRevealOpen.length * 0.065).toFixed(2)}</span>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setBulkIcpRevealOpen(false)} data-testid="bulk-icp-cancel-btn">
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={confirmBulkIcpReveal}
                disabled={bulkIcpRevealing}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="bulk-icp-confirm-btn"
              >
                {bulkIcpRevealing ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Zap className="h-3.5 w-3.5 mr-1.5" />
                )}
                Reveal {bulkIcpRevealOpen.length} Leads
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
